import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Box, Stack, Typography } from '@mui/material';
import { PlatformEntity } from '@provision/entity-types';
import { useMemo, useState } from 'react';
import { UseFieldArrayReturn, useFormContext } from 'react-hook-form';
import { DynamicIcon } from 'src/components';
import { EllipsesMenu } from 'src/components/EllipsesMenu/ellipses-menu';
import { PLATFORM_PARTNER_ID } from 'src/constants/platform';
import { CustomAttributeGroup } from 'src/graphql/entities/custom-attributes/custom-attributes';
import { useGlobalStore } from 'src/store';
import { CustomAttributeGroupForm } from './custom-attribute-group-form';

type DraggableGroupProps = {
  group: Partial<CustomAttributeGroup> & { id: string };
  index: number;
  entity?: PlatformEntity;
  fieldArrayMethods: UseFieldArrayReturn<
    Record<string, Record<string, Partial<CustomAttributeGroup>[] | undefined>>,
    `${string}.${number}`
  >;
};

export const DraggableGroup = ({
  group,
  entity,
  index,
  fieldArrayMethods,
}: DraggableGroupProps) => {
  const selectedPartnerId = useGlobalStore.use.selectedPartnerId();

  const { formState } =
    useFormContext<
      Record<string, Record<string, Partial<CustomAttributeGroup>[] | undefined>>
    >();

  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable(
    { id: group.id },
  );
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const isMultiTenancyMode = useGlobalStore.use.isMultiTenancyMode();

  const hasAccessToMenu =
    !isMultiTenancyMode ||
    selectedPartnerId === PLATFORM_PARTNER_ID ||
    group.partnerId !== PLATFORM_PARTNER_ID;

  const menuOptions = useMemo(
    () => [
      {
        label: 'Edit',
        testId: 'custom-attribute-group-edit',
        onClick: () => {
          setIsMenuOpen(false);
          fieldArrayMethods.update(index, {
            ...group,
            isEditing: true,
          });
        },
      },
      {
        label: 'Delete',
        testId: 'custom-attribute-group-delete',
        onClick: () => {
          setIsMenuOpen(false);
          fieldArrayMethods.remove(index);
        },
      },
    ],
    [fieldArrayMethods, group, index],
  );

  if (group.isEditing) {
    return (
      <CustomAttributeGroupForm
        onSubmit={(value) => {
          if (!value && fieldArrayMethods.fields[index].isNew === true) {
            fieldArrayMethods.remove(index);
            return;
          }

          if (Object.keys(formState.errors).length) {
            return;
          }

          fieldArrayMethods.update(index, {
            ...group,
            displayName: value,
            isEditing: false,
            isNew: false,
          });
        }}
        partnerId={group.partnerId!}
        entity={entity}
        index={index}
      />
    );
  }

  return (
    <Stack
      flexDirection="row"
      alignItems="center"
      onMouseEnter={() => {
        setIsMenuOpen(true);
      }}
      onMouseLeave={() => {
        setIsMenuOpen(false);
      }}
      ref={setNodeRef}
      data-testid="custom-attribute-group-row"
      sx={{
        opacity: isDragging ? 0.5 : 1,
        transform: CSS.Transform.toString(transform),
        transition,
      }}
    >
      {hasAccessToMenu ? (
        <Box
          {...attributes}
          {...listeners}
          data-testid="custom-attribute-group-drag-handle"
          sx={{ cursor: isDragging ? 'grabbing' : 'grab' }}
        >
          <DynamicIcon
            name="drag_indicator"
            sx={{
              display: 'flex',
            }}
          />
        </Box>
      ) : null}

      <Typography
        variant="body2"
        textOverflow="ellipsis"
        whiteSpace="nowrap"
        overflow="hidden"
        data-testid="custom-attribute-group-name"
        sx={{
          padding: 1,
        }}
      >
        {group.displayName}
      </Typography>

      {isMenuOpen && hasAccessToMenu ? (
        <EllipsesMenu
          options={menuOptions}
          icon="more_horiz"
          triggerTestId="custom-attribute-group-menu"
        />
      ) : null}
    </Stack>
  );
};
